import { useState, useEffect } from "react";
import {
  Box,
  Container,
  Paper,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ViewIcon from "@mui/icons-material/Visibility";
import SaveIcon from "@mui/icons-material/Save";

import useApi from "../../hooks/useApi";
import useLocalization from "../../hooks/useLocalization";
import PageHeader from "../layout/PageHeader";
import DocumentationEditor from "../documentation/DocumentationEditor";
import DocumentationForm from "../documentation/DocumentationForm";
import MarkdownViewer from "../documentation/MarkdownViewer";

const DocumentationManagement = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Modal/Form state
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [currentDoc, setCurrentDoc] = useState(null);
  const [viewDoc, setViewDoc] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState({
    key: "",
    title: "",
    description: "",
    type: "user",
    tags: [],
    pageReference: [],
    language: "en",
    content: "",
  });

  const { postData } = useApi();
  const { getLabel } = useLocalization();

  // Load all documents
  useEffect(() => {
    loadDocumentation();
  }, []);

  const loadDocumentation = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await postData({
        url: "/api/entity",
        entityName: "Documentation",
        action: "list",
        data: {},
      });

      if (response && response.data) {
        setDocs(Array.isArray(response.data) ? response.data : []);
      }
    } catch (err) {
      console.error("Error loading documentation:", err);
      setError("Failed to load documentation");
    } finally {
      setLoading(false);
    }
  };

  const handleNewDoc = () => {
    setCurrentDoc(null);
    setFormData({
      key: "",
      title: "",
      description: "",
      type: "user",
      tags: [],
      pageReference: [],
      language: "en",
      content: "",
    });
    setTabValue(0);
    setOpenDialog(true);
  };

  const handleEditDoc = async (doc) => {
    try {
      setLoading(true);

      // Fetch full document with content
      const response = await postData({
        url: "/api/entity",
        entityName: "Documentation",
        action: "getById",
        data: { id: doc.id || doc._id },
      });

      if (response && response.data) {
        setCurrentDoc(response.data);
        setFormData(response.data);
        setTabValue(0);
        setOpenDialog(true);
      }
    } catch (err) {
      console.error("Error loading document:", err);
      setError("Failed to load document details");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.key || !formData.title || !formData.type) {
        setError("Please fill in required fields: Key, Title, Type");
        return;
      }

      setSaving(true);
      setError(null);

      let response;

      if (currentDoc?.id) {
        // Update existing
        response = await postData({
          url: "/api/entity",
          entityName: "Documentation",
          action: "update",
          data: {
            id: currentDoc.id,
            title: formData.title,
            description: formData.description,
            type: formData.type,
            tags: formData.tags,
            pageReference: formData.pageReference,
            language: formData.language,
            content: formData.content,
          },
        });
      } else {
        // Create new
        response = await postData({
          url: "/api/entity",
          entityName: "Documentation",
          action: "create",
          data: {
            key: formData.key,
            title: formData.title,
            description: formData.description,
            type: formData.type,
            tags: formData.tags,
            pageReference: formData.pageReference,
            language: formData.language,
            content: formData.content,
          },
        });
      }

      setSuccess(
        currentDoc
          ? "Documentation updated successfully"
          : "Documentation created successfully",
      );
      setOpenDialog(false);
      loadDocumentation();
    } catch (err) {
      console.error("Error saving documentation:", err);
      setError(err.message || "Failed to save documentation");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (
      !window.confirm("Are you sure you want to delete this documentation?")
    ) {
      return;
    }

    try {
      setError(null);
      await postData({
        url: "/api/entity",
        entityName: "Documentation",
        action: "delete",
        data: { id: docId },
      });

      setSuccess("Documentation deleted successfully");
      loadDocumentation();
    } catch (err) {
      console.error("Error deleting documentation:", err);
      setError("Failed to delete documentation");
    }
  };

  const handleViewDoc = async (doc) => {
    try {
      setError(null);
      // Fetch full document with content
      const response = await postData({
        url: "/api/entity",
        entityName: "Documentation",
        action: "getById",
        data: { id: doc.id },
      });

      setViewDoc(response && response.data ? response.data : response);
      setOpenViewDialog(true);
    } catch (err) {
      console.error("Error fetching documentation:", err);
      setError("Failed to load documentation");
    }
  };

  return (
    <>
      <PageHeader pageName="documentation" />

      <Container
        maxWidth="lg"
        sx={{ py: 3 }}
      >
        <Stack spacing={3}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h5"
              sx={{ fontWeight: "bold" }}
            >
              Documentation Management
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleNewDoc}
              disabled={loading}
            >
              New Documentation
            </Button>
          </Box>

          {/* Messages */}
          {error && (
            <Alert
              severity="error"
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}
          {success && (
            <Alert
              severity="success"
              onClose={() => setSuccess(null)}
            >
              {success}
            </Alert>
          )}

          {/* Table */}
          {loading ? (
            <LinearProgress />
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell>Key</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Pages</TableCell>
                    <TableCell>Tags</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {docs.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        align="center"
                        sx={{ py: 3 }}
                      >
                        <Typography color="textSecondary">
                          No documentation yet
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    docs.map((doc) => (
                      <TableRow
                        key={doc.id || doc.key}
                        hover
                      >
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace" }}
                          >
                            {doc.key}
                          </Typography>
                        </TableCell>
                        <TableCell>{doc.title}</TableCell>
                        <TableCell>
                          <Chip
                            label={doc.type}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {doc.pageReference?.length > 0 ? (
                            <Stack
                              direction="row"
                              spacing={0.5}
                            >
                              {doc.pageReference.map((ref, idx) => (
                                <Chip
                                  key={idx}
                                  label={ref.pageName}
                                  size="small"
                                />
                              ))}
                            </Stack>
                          ) : (
                            <Typography
                              variant="caption"
                              color="textSecondary"
                            >
                              None
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {doc.tags?.length > 0 ? (
                            <Box
                              sx={{
                                display: "flex",
                                gap: 0.5,
                                flexWrap: "wrap",
                              }}
                            >
                              {doc.tags.map((tag, idx) => (
                                <Chip
                                  key={idx}
                                  label={tag}
                                  size="small"
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          ) : (
                            <Typography
                              variant="caption"
                              color="textSecondary"
                            >
                              None
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            startIcon={<ViewIcon />}
                            onClick={() => handleViewDoc(doc)}
                          >
                            View
                          </Button>
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleEditDoc(doc)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            startIcon={<DeleteIcon />}
                            color="error"
                            onClick={() => handleDeleteDoc(doc.id)}
                          >
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </Container>

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { maxHeight: "90vh" },
        }}
      >
        <DialogTitle>
          {currentDoc ? "Edit Documentation" : "Create New Documentation"}
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ pt: 2 }}>
            <Tabs
              value={tabValue}
              onChange={(e, val) => setTabValue(val)}
            >
              <Tab label="Metadata" />
              <Tab label="Content" />
            </Tabs>

            <Box sx={{ pt: 2 }}>
              {tabValue === 0 ? (
                <DocumentationForm
                  data={formData}
                  onChange={setFormData}
                />
              ) : (
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 2 }}
                  >
                    Markdown Content
                  </Typography>
                  <DocumentationEditor
                    value={formData.content}
                    onChange={(content) =>
                      setFormData({ ...formData, content })
                    }
                    onFileUpload={(fileInfo) => {
                      setSuccess(`Loaded file: ${fileInfo.fileName}`);
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <MarkdownViewer
        open={openViewDialog}
        onClose={() => setOpenViewDialog(false)}
        docs={viewDoc ? [viewDoc] : []}
        selectedDoc={viewDoc}
      />
    </>
  );
};

export default DocumentationManagement;
